// Branded toast + error dialog system.
//
// Replaces window.alert() and bare console.error across the app. Use:
//
//   const toast = useToast();
//   toast.error('Could not save', 'The PIN is already in use.');
//   toast.success('Saved');
//   toast.info('Heads up', 'Your session expires in 5 minutes.');
//
// Routing:
//   - error  → centred AlertDialog (modal, blocking, manual dismiss)
//              — visual language ported from a 21st.dev component
//                ("notification-alert-dialog") and recoloured to the
//                moreece.com copper-on-cream palette. Disruptive on
//                purpose: errors are usually data losses to acknowledge.
//   - success/info → corner toast (top-right, auto-dismiss, non-blocking).
//
// Multiple errors don't stack visually — newest replaces. If two writes
// fail in the same beat (unusual), only the second is shown. Call sites
// already fire one error per action, so stacking would be louder than
// helpful.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const SERIF = "'Tiempos', Georgia, 'Times New Roman', serif";
const SANS  = "Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
const MONO  = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

// Corner-toast palette (success/info only — errors use the dialog).
const KIND = {
  success: { accent: '#036E43', bg: '#ECF6F0', border: '#D6EADF', label: 'Done', ttl: 6000 },
  info:    { accent: '#0F1419', bg: '#FAFAF8', border: '#EFEDE7', label: 'Note', ttl: 6000 },
};

let _id = 0;
function nextId() { return ++_id; }

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [errorDialog, setErrorDialog] = useState(null); // {title, body} or null

  const dismiss = useCallback(id => {
    setToasts(ts => ts.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((kind, title, body) => {
    const id = nextId();
    const k = KIND[kind] || KIND.info;
    setToasts(ts => [...ts, { id, kind, title, body }]);
    setTimeout(() => dismiss(id), k.ttl);
    return id;
  }, [dismiss]);

  const api = {
    error:   (title, body) => setErrorDialog({ title, body }),
    success: (title, body) => pushToast('success', title, body),
    info:    (title, body) => pushToast('info',    title, body),
    dismiss,
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {/* Corner toasts — success/info */}
      {createPortal(
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 10000,
          display: 'flex', flexDirection: 'column', gap: 10,
          pointerEvents: 'none',
          maxWidth: 380,
        }}>
          {toasts.map(t => <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />)}
        </div>,
        document.body,
      )}
      {/* Error dialog — centred modal, dismissible */}
      {errorDialog && (
        <ErrorDialog
          title={errorDialog.title}
          body={errorDialog.body}
          onClose={() => setErrorDialog(null)}
        />
      )}
    </ToastCtx.Provider>
  );
}

function ToastCard({ toast, onDismiss }) {
  const k = KIND[toast.kind] || KIND.info;
  const [enter, setEnter] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setEnter(true)); }, []);

  return (
    <div
      onClick={onDismiss}
      role="alert"
      style={{
        pointerEvents: 'auto',
        background: k.bg,
        border: `1px solid ${k.border}`,
        borderLeft: `3px solid ${k.accent}`,
        borderRadius: 10,
        padding: '14px 16px 14px 18px',
        boxShadow: '0 8px 24px -8px rgba(15,20,25,0.18), 0 1px 0 rgba(15,20,25,0.04)',
        transform: enter ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.98)',
        opacity: enter ? 1 : 0,
        transition: 'transform 180ms cubic-bezier(0.2,0.8,0.2,1), opacity 180ms',
        cursor: 'pointer',
        minWidth: 280,
      }}>
      <div style={{
        fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em',
        textTransform: 'uppercase', color: k.accent, marginBottom: 6,
        fontWeight: 500,
      }}>{k.label}</div>
      <div style={{
        fontFamily: SERIF, fontSize: 16, fontWeight: 400, color: '#0F1419',
        letterSpacing: '-0.01em', lineHeight: 1.25,
      }}>{toast.title}</div>
      {toast.body && (
        <div style={{
          fontFamily: SANS, fontSize: 13,
          color: '#5C6166', lineHeight: 1.45, marginTop: 6,
        }}>{toast.body}</div>
      )}
    </div>
  );
}

// ── ErrorDialog ─────────────────────────────────────────────────────────
// Visual port of the 21st.dev notification-alert-dialog (centred Radix
// AlertDialog with header icon + bordered card + footer actions).
// Colour-shifted from indigo → copper red to match the moreece.com
// system palette and to read unmistakably as "error" rather than "info".
//
// Behaviour:
//   - dark backdrop with blur, click-through to dismiss
//   - ESC also dismisses
//   - one button (Got it) — errors are acknowledgements, not choices
//   - "Copy details" link for the body so debugging via support is easy
//   - manual dismiss only (no auto-timeout: a modal that disappears
//     while you're reading it is worse than no modal)
function ErrorDialog({ title, body, onClose }) {
  const [enter, setEnter] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setEnter(true));
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function copyDetails() {
    const text = title + (body ? `\n\n${body}` : '');
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => {});
  }

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="moreece-error-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 11000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        background: 'rgba(15,20,25,0.55)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        opacity: enter ? 1 : 0,
        transition: 'opacity 180ms',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px, calc(100vw - 48px))',
          background: '#FFFDF8',
          border: '2px solid #E8B4A6',
          borderRadius: 16,
          boxShadow: '0 30px 80px -20px rgba(15,20,25,0.45), 0 0 0 1px rgba(184,92,60,0.08)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          transform: enter ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
          opacity: enter ? 1 : 0,
          transition: 'transform 220ms cubic-bezier(0.2,0.8,0.2,1), opacity 220ms',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 22px 4px',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: '#FBEAE5', color: '#A03422',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <AlertCircleIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: '#B85C3C', marginBottom: 4,
              fontWeight: 500,
            }}>Issue</div>
            <h2
              id="moreece-error-title"
              style={{
                margin: 0, fontFamily: SERIF, fontSize: 20, fontWeight: 400,
                lineHeight: 1.25, letterSpacing: '-0.01em', color: '#0F1419',
              }}
            >{title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#8B9094', padding: 6, marginTop: -4, marginRight: -6,
              borderRadius: 6, lineHeight: 1,
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        {body && (
          <div style={{
            padding: '8px 22px 16px 70px',
            fontFamily: SANS, fontSize: 13.5, color: '#3E4148',
            lineHeight: 1.55, whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere', wordBreak: 'break-word',
          }}>{body}</div>
        )}

        {/* Footer */}
        <div style={{
          padding: '14px 18px',
          borderTop: '1px solid #F1E8E2',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(184,92,60,0.03) 100%)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {body && (
            <button
              onClick={copyDetails}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#6E7278', fontFamily: MONO, fontSize: 10,
                letterSpacing: '0.06em', textTransform: 'uppercase', padding: 0,
              }}
            >{copied ? 'copied' : 'copy details'}</button>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 8,
              background: '#A03422', color: '#FFFDF8',
              border: 'none', cursor: 'pointer',
              fontFamily: SANS, fontSize: 13, fontWeight: 500,
              transition: 'background 120ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#8B2A1B'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#A03422'; }}
          >Got it</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Inline icons — avoids pulling lucide-react just for two glyphs.
function AlertCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
